<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('unblacklist.unblacklist') }}
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div
                v-if="!isLoaded"
                class="row"
            >
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div class="box-body">
                            <img
                                alt="preloader"
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"/>
                        </div>
                    </div>
                </div>
            </div>

            <div v-show="isLoaded">
                <UnblacklistProcesses
                    v-show="isLoaded"
                    :isLoading="isLoading"
                    :isUploading="isUploading"
                    :processes="unblacklistProcesses"
                    :uploadingProgressText="uploadingProgressText"
                    :uploadingProgressValue="uploadingProgressValue"
                    @confirmProcess="processData = $event"
                    @createProcess="createProcess"
                    @startProcess="startProcess"
                />
            </div>
        </section>

        <ModalDialog
            id="confirm-modal"
            ref="confirmModal"
            :isLoading="isLoading"
            class="modal-danger"
            size="sm"
            @closed="processData = {}"
            @submit="submitProcess"
        >
            <template #header>
                {{
                    processData.status === PROCESS_STATUS_PROCESSING
                        ? `${$t('cancel')} ${$t('process')}?`
                        : `${$t('delete')} ${$t('process')}?`
                }}
            </template>

            <template #footer="{ close, submit }">
                <button
                    class="btn btn-sm btn-default"
                    data-dismiss="modal"
                    type="button"
                    @click="close"
                >
                    {{ $t('close') }}
                </button>

                <button
                    :disabled="isLoading"
                    class="btn btn-sm btn-danger"
                    @click="submit"
                >
                    <i
                        v-if="isLoading"
                        class="fa fa-refresh fa-spin"
                    />
                    <span>{{ processData.status === PROCESS_STATUS_PROCESSING ? $t('cancel') : $t('delete') }}</span>
                </button>
            </template>
        </ModalDialog>

        <ModalDialog
            id="process-modal"
            ref="processModal"
            @before-hide="clearSelectComponents"
        >
            <template #header>{{ $t('unblacklist.unblacklist') }}</template>

            <template #body>
                <table class="table table-striped table-hover">
                    <tbody>
                    <tr class="modal-export-item">
                        <td class="entities-td">
                            <DateRangeSingleComponentVue
                                ref="dateBeforePicker"
                                :append-to-body="true"
                                :disable-future-dates="true"
                                class-input="form-control"
                                class-main-block="form-group main-date-range-single"
                                placeholder="Date before"
                                title="Date before"
                                @change="dateBefore = $event"
                            />
                        </td>
                        <td class="text-right">
                            <button
                                :disabled="disableButtonSend"
                                :title="$t('unblacklist.startUnblacklist')"
                                class="btn btn-sm btn-primary button-start"
                                type="button"
                                @click="createProcess()"
                            >
                                {{ $t('unblacklist.startUnblacklist') }}
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </template>

            <template #footer="{ close }">
                <button
                    class="btn btn-sm btn-default"
                    data-dismiss="modal"
                    type="button"
                    @click="close"
                >
                    {{ $t('close') }}
                </button>
            </template>
        </ModalDialog>

    </div>
</template>
<script>
import EventBus from '@/event-bus';
import ModalDialog from '@/components/modals/ModalDialog';
import processMixin from '@/mixins/processMixin';
import UnblacklistProcesses from '@/components/additional/unblacklist/UnblacklistProcesses';
import DateRangeSingleComponentVue from "@/components/partials/DateRangeSingleComponentVue";

export default {
    name: 'AdditionalUnblacklist',
    components: {
        DateRangeSingleComponentVue,
        UnblacklistProcesses,
        ModalDialog,
    },
    mixins: [processMixin],
    data() {
        return {
            isLoading: false,
            isLoaded: false,
            timeout: 0,
            unblacklistProcesses: [],
            processData: {},
            fetchProcessCancelToken: null,
            isUploading: false,
            uploadingProgressValue: 0,
            uploadingProgressText: '',
            dateBefore: null,
        };
    },
    computed: {
        disableButtonSend() {
            return ! this.dateBefore || this.isLoading;
        },
    },
    async created() {
        await this.fetchProcesses();
        this.isLoaded = true;
    },
    methods: {
        async fetchProcesses() {
            this.fetchProcessCancelToken = axios.CancelToken.source();

            try {
                const {
                    data: { processes },
                } = await axios.get('/additional/unblacklist/data', {
                    cancelToken: this.fetchProcessCancelToken.token,
                });

                this.unblacklistProcesses = processes.map((process) => {
                    process.isShow = true;
                    return process;
                });

                this.checkProgress();
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.fetchProcessCancelToken = null;
            }
        },
        async createProcess() {
            if (this.fetchProcessCancelToken) {
                this.fetchProcessCancelToken.cancel('createProcess: cancel');
            }

            const requestData = new FormData();
            requestData.append('dateBefore', this.dateBefore);

            this.isUploading = true;
            this.isLoading = true;

            try {
                const {
                    data: { process },
                } = await axios.post('/additional/unblacklist/data/process', requestData);

                this.unblacklistProcesses.unshift(process);

                setTimeout(() => {
                    this.$set(this.unblacklistProcesses, 0, {
                        ...process,
                        isShow: true,
                    });
                }, 1);

                this.checkProgress();
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
                this.isUploading = false;

                if (this.$refs.processModal) {
                    this.$refs.processModal.close();
                }
            }
        },
        checkProgress() {
            const isProgress = !!this.unblacklistProcesses.find((process) => {
                return process.status === this.PROCESS_STATUS_PROCESSING;
            });

            clearTimeout(this.timeout);

            if (isProgress) {
                this.timeout = setTimeout(() => {
                    this.fetchProcesses();
                }, 5000);
            }
        },
        async updateProcess(processId, status) {
            this.isLoading = true;

            try {
                const {
                    data: { process },
                } = await axios.put(`/additional/unblacklist/data/process/${processId}`, {
                    status,
                });

                this.unblacklistProcesses.forEach((unblacklistProcesses) => {
                    if (unblacklistProcesses.id === process.id) {
                        unblacklistProcesses.status = status;
                    }
                });

                this.checkProgress();
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
            }
        },
        async deleteProcess(processId) {
            this.isLoading = true;

            try {
                await axios.delete(`/additional/unblacklist/data/process/${processId}`);
                this.unblacklistProcesses.forEach((process, idx) => {
                    if (process.id === processId) {
                        process.isShow = false;
                        setTimeout(() => {
                            this.unblacklistProcesses.splice(idx, 1);
                            this.checkProgress();
                        }, 1000);
                    }
                });

            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoading = false;
            }
        },
        startProcess(processData) {
            if (processData.status !== this.PROCESS_STATUS_CANCELED) return;

            this.updateProcess(processData.id, this.PROCESS_STATUS_PROCESSING);
        },
        async submitProcess() {
            if (this.processData.status === this.PROCESS_STATUS_PROCESSING) {
                await this.updateProcess(this.processData.id, this.PROCESS_STATUS_CANCELED);
            } else {
                await this.deleteProcess(this.processData.id);
            }

            this.$refs.confirmModal.close();
        },
        clearSelectComponents() {
            this.categoryData = {};
            this.selectedEntities = {};
            this.$refs.dateBeforePicker.clear();
            this.dateBefore = null;
        },
    },
};
</script>
<style src="./AdditionalUnblacklist.css" />
